import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import CallbackRequestCard from 'src/app/interfaces/card/requests/CallbackRequestCard';
import { Session } from 'src/app/interfaces/session-data';
import { TdsParams } from 'src/app/interfaces/tds/TdsParams';
import { AuthoriseResponseCard } from 'src/app/interfaces/card/responses/AuthoriseResponseCard';
import { TransactionStatus } from 'src/app/interfaces/transaction/TransactionStatus';
import { CardService } from 'src/app/services/card/card.service';
import { TransactionService } from 'src/app/services/transaction/transaction.service';
import { SessionService } from 'src/app/services/session/session.service';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';

@Component({
  selector: 'app-tds-version-two',
  templateUrl: './tds.component.html',
  styleUrls: ['./tds.component.scss']
})
export class TdsComponent implements OnInit {
  loading = true;
  merchantCustomTheme: HTMLStyleElement;
  cardSvgEl: SafeHtml | null = null;
  tdsAuthed: boolean;
  callbackRequestCard: CallbackRequestCard;
  success = new Subject<boolean>();
  tdsParams: TdsParams;
  pollingInterval: any;
  statesToReturnOn = ['SETTLED', 'FAILED'];
  session: Session;

  constructor(
    private sanitizer: DomSanitizer,
    private cardService: CardService,
    private router: Router,
    private analyticsService: AnalyticsService,
    private transactionService: TransactionService,
    private sessionService: SessionService
  ) {
    // Remove acs form session data
    sessionStorage.removeItem('acs-form');
    
    // Subscribe to success subject
    this.success.subscribe(
      () => {
        this.success.complete();
      },
      () => this.navigateToStatus()
    );
  }

  ngOnInit(): void {
    console.log('TdsV2Component - ngOnInit called, URL:', window.location.href);
    console.log('TdsV2Component - isInIframe:', this.isInIframe());
    
    // Check if we're running in an iframe (3DS callback)
    if (this.isInIframe()) {
      console.log('TdsV2Component - Handling iframe callback');
      this.handleIframeCallback();
      return;
    }
    
    // // Set card SVG element from session storage
    // const cardSvgEl = sessionStorage.getItem('card_svg_el');
    // if (cardSvgEl) {
    //   this.cardSvgEl = this.sanitizer.bypassSecurityTrustHtml(cardSvgEl);
    // }
    
    // Handle session setup if no session token exists
    if (sessionStorage.getItem('sessionToken') == null) {
      const sessionId = this.getQueryParam('sessionId');
      if (sessionId) {
        this.sessionService.getSessionTokenWithNoSession(sessionId)
          .subscribe({
            next: (data: Session) => {
              sessionStorage.setItem('sessionData', JSON.stringify(data));
              sessionStorage.setItem('sessionToken', sessionId);
              
              // Track TDS page load
              this.analyticsService.trackPageLoad(
                sessionId,
                data,
                window.location.href,
                'TdsV2Component'
              ).subscribe();
              
              this.processTransaction();
            },
            error: () => this.navigateToStatus()
          });
      } else {
        this.navigateToStatus();
      }
    } else {
      this.processTransaction();
    }
  }

  private getQueryParam(param: string): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(param);
  }

  private processTransaction(): void {
    const sessionToken = sessionStorage.getItem('sessionToken');
    if (!sessionToken) {
      this.navigateToStatus();
      return;
    }

    this.transactionService.getTransactionStatusCard(sessionToken)
      .subscribe((data: TransactionStatus) => {
        // Track transaction status check
        this.analyticsService.trackEvent({
          eventCategory: 'TDS',
          eventAction: 'Transaction Status Check',
          eventLabel: data?.transactionState || 'Unknown',
          sessionToken,
          paymentMethod: 'CARD',
          eventComponentName: 'TdsV2Component'
        }).subscribe();
        
        if (data?.transactionState === 'TDS_AUTHENTICATED') {
          this.tdsAuthed = true;
          this.session = JSON.parse(sessionStorage.getItem('sessionData') || '{}');
          
          if (this.session.flow !== 'CARD_VERIFICATION') {
            this.authoriseCard();
          } else {
            this.navigateToStatus();
          }
        } else {
          this.navigateToStatus();
        }
      }, (error: HttpErrorResponse) => {
        // Track error
        this.analyticsService.trackEvent({
          eventCategory: 'TDS',
          eventAction: 'Transaction Status Error',
          eventLabel: `HTTP ${error.status}: ${error.message}`,
          sessionToken,
          paymentMethod: 'CARD',
          eventComponentName: 'TdsV2Component'
        }).subscribe();
        
        this.navigateToStatus();
      });
  }

  private navigateToStatus(): void {
    this.loading = false;
    
    // Track TDS completion analytics
    const sessionToken = sessionStorage.getItem('sessionToken');
    if (sessionToken) {
      this.analyticsService.trackEvent({
        eventCategory: 'TDS',
        eventAction: 'Authentication Complete',
        eventLabel: 'Card TDS Flow',
        sessionToken,
        paymentMethod: 'CARD',
        eventComponentName: 'TdsV2Component'
      }).subscribe();
    }
    
    this.router.navigate([`${this.getVirtualV2BaseUrl()}/transaction-status`]);
  }

  private getVirtualV2BaseUrl(): string {
    return '/virtual-v2';
  }

  private authoriseCard(): void {
    const sessionToken = sessionStorage.getItem('sessionToken');
    if (!sessionToken) {
      this.navigateToStatus();
      return;
    }

    this.cardService.authoriseTransaction(sessionToken)
      .subscribe((dataAuthorise: AuthoriseResponseCard) => {
        // Track successful authorization
        this.analyticsService.trackEvent({
          eventCategory: 'TDS',
          eventAction: 'Card Authorization Success',
          eventLabel: 'Authorization Complete',
          sessionToken,
          paymentMethod: 'CARD',
          eventComponentName: 'TdsV2Component'
        }).subscribe();
        
        this.navigateToStatus();
      }, (err: HttpErrorResponse) => {
        if (err.status === 409) {
          // Track polling start
          this.analyticsService.trackEvent({
            eventCategory: 'TDS',
            eventAction: 'Authorization Pending',
            eventLabel: 'Starting Polling',
            sessionToken,
            paymentMethod: 'CARD',
            eventComponentName: 'TdsV2Component'
          }).subscribe();
          
          this.startPolling();
        } else {
          // Track authorization error
          this.analyticsService.trackEvent({
            eventCategory: 'TDS',
            eventAction: 'Card Authorization Error',
            eventLabel: `HTTP ${err.status}: ${err.message}`,
            sessionToken,
            paymentMethod: 'CARD',
            eventComponentName: 'TdsV2Component'
          }).subscribe();
          
          this.navigateToStatus();
        }
      });
  }

  private startPolling(): void {
    const timeout = 900; // 15 minutes
    const interval = 30; // 30 seconds
    let count = 0;
    
    // Set overall timeout
    setTimeout(() => {
      this.clearIntervalCustom(this.pollingInterval);
    }, timeout * 1000);
    
    // Start polling
    this.pollingInterval = setInterval(() => {
      count++;
      const sessionToken = sessionStorage.getItem('sessionToken');
      
      if (sessionToken) {
        this.transactionService.getTransactionStatusCard(sessionToken)
          .subscribe((data: TransactionStatus) => {
            if (this.statesToReturnOn.includes(data.transactionState)) {
              // Track polling completion
              this.analyticsService.trackEvent({
                eventCategory: 'TDS',
                eventAction: 'Polling Complete',
                eventLabel: data.transactionState,
                sessionToken,
                paymentMethod: 'CARD',
                eventComponentName: 'TdsV2Component'
              }).subscribe();
              
              this.clearIntervalCustom(this.pollingInterval);
            }
          }, () => {
            // Track polling error
            this.analyticsService.trackEvent({
              eventCategory: 'TDS',
              eventAction: 'Polling Error',
              eventLabel: 'Status Check Failed',
              sessionToken,
              paymentMethod: 'CARD',
              eventComponentName: 'TdsV2Component'
            }).subscribe();
            
            // Error in polling, stop and navigate
            this.clearIntervalCustom(this.pollingInterval);
          });
      } else {
        this.clearIntervalCustom(this.pollingInterval);
      }
    }, interval * 1000);
  }

  private clearIntervalCustom(interval: any): void {
    if (interval) {
      clearInterval(interval);
    }
    
    // Track polling timeout/completion
    const sessionToken = sessionStorage.getItem('sessionToken');
    if (sessionToken) {
      this.analyticsService.trackEvent({
        eventCategory: 'TDS',
        eventAction: 'Polling Ended',
        eventLabel: 'Timeout or Final State Reached',
        sessionToken,
        paymentMethod: 'CARD',
        eventComponentName: 'TdsV2Component'
      }).subscribe();
    }
    
    this.navigateToStatus();
  }

  /**
   * Check if component is running inside an iframe
   */
  private isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      // If we can't access window.top due to cross-origin restrictions,
      // we're likely in an iframe
      return true;
    }
  }

  /**
   * Handle 3DS callback when running in iframe context
   * Send completion message to parent instead of trying to navigate
   */
  private handleIframeCallback(): void {
    const sessionToken = this.getQueryParam('sessionId') || sessionStorage.getItem('sessionToken');
    
    // Show loading message to user
    this.loading = true;
    
    // Track iframe callback
    if (sessionToken) {
      this.analyticsService.trackEvent({
        eventCategory: '3DS Flow',
        eventAction: '3DS Iframe Callback Angular',
        eventLabel: 'Bank redirect received in iframe - Angular component loaded',
        sessionToken,
        paymentMethod: 'CARD',
        eventComponentName: 'TdsV2Component'
      }).subscribe();
    }
    
    // Send completion message to parent window immediately
    this.sendCompletionMessageToParent();
    
    // Also try to prevent further Angular initialization
    this.loading = false;
  }

  /**
   * Send 3DS completion message to parent window
   */
  private sendCompletionMessageToParent(): void {
    try {
      const sessionToken = this.getQueryParam('sessionId') || sessionStorage.getItem('sessionToken');
      const message = {
        status: 'COMPLETED',
        paymethod: 'CARD',
        source: '3DS_CALLBACK_ANGULAR',
        url: window.location.href,
        sessionId: sessionToken
      };
      
      // Send to parent with current origin and also try wildcard for cross-origin
      try {
        window.parent.postMessage(message, window.location.origin);
      } catch (e1) {
        // Try with wildcard origin as fallback
        try {
          window.parent.postMessage(message, '*');
        } catch (e2) {
          console.error('Both postMessage attempts failed:', e1, e2);
        }
      }
      
      // Track the postMessage sent
      if (sessionToken) {
        this.analyticsService.trackEvent({
          eventCategory: '3DS Flow',
          eventAction: '3DS Completion Message Sent Angular',
          eventLabel: `Angular component sent message: ${JSON.stringify(message)}`,
          sessionToken,
          paymentMethod: 'CARD',
          eventComponentName: 'TdsV2Component'
        }).subscribe();
      }
      
      console.log('3DS completion message sent to parent from Angular component:', message);
      
      // Show success message briefly
      setTimeout(() => {
        document.body.innerHTML = `
          <div style="font-family: Arial, sans-serif; text-align: center; padding: 50px; background: #f5f5f5; height: 100vh; margin: 0;">
            <div style="background: white; padding: 30px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); display: inline-block;">
              <h3 style="color: #27AE60; margin-bottom: 15px;">✓ Authentication Complete</h3>
              <p style="color: #666;">Returning to payment...</p>
            </div>
          </div>
        `;
      }, 500);
      
    } catch (error) {
      console.error('Failed to send completion message to parent:', error);
      
      // Track the error
      const sessionToken = this.getQueryParam('sessionId') || sessionStorage.getItem('sessionToken');
      if (sessionToken) {
        this.analyticsService.trackEvent({
          eventCategory: 'Error Handling',
          eventAction: '3DS PostMessage Failed Angular',
          eventLabel: `Angular component failed to send message: ${error}`,
          sessionToken,
          paymentMethod: 'CARD',
          eventComponentName: 'TdsV2Component'
        }).subscribe();
      }
      
      // Show error message
      document.body.innerHTML = `
        <div style="font-family: Arial, sans-serif; text-align: center; padding: 50px; background: #f5f5f5; height: 100vh; margin: 0;">
          <div style="background: white; padding: 30px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); display: inline-block;">
            <h3 style="color: #E74C3C; margin-bottom: 15px;">⚠ Communication Error</h3>
            <p style="color: #666;">Please close this window and try again.</p>
          </div>
        </div>
      `;
    }
  }
}
